import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, X, Save, Upload, ImagePlus, ShoppingBag, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import {
  fetchMerch,
  adminUpdateMerchSettings,
  adminCreateMerchProduct,
  adminUpdateMerchProduct,
  adminDeleteMerchProduct,
  adminUploadImage,
  resolveImageUrl,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", description: "", price: 0, image_url: "", buy_url: "", badge: "", in_stock: true, sort_order: 999 };

export const AdminMerch = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchMerch();
      setSettings(data.settings);
      setProducts(data.products || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !settings) {
    return <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading…</div>;
  }

  const update = (field) => (val) => setSettings((s) => ({ ...s, [field]: val }));
  const togglePreview = (val, idx) => setSettings((s) => {
    const list = [...(s.preview_items || [])];
    list[idx] = val;
    return { ...s, preview_items: list };
  });
  const addPreview = () => setSettings((s) => ({ ...s, preview_items: [...(s.preview_items || []), "New Item"] }));
  const removePreview = (idx) => setSettings((s) => {
    const list = [...(s.preview_items || [])];
    list.splice(idx, 1);
    return { ...s, preview_items: list };
  });

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminUpdateMerchSettings(settings, token);
      toast.success("Merch settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleLive = async () => {
    const next = settings.status === "live" ? "coming_soon" : "live";
    if (next === "live" && products.length === 0) {
      const ok = window.confirm("You have no products yet — the live shop will be empty. Add products first?\nOK = switch anyway, Cancel = stay in Coming Soon mode.");
      if (!ok) return;
    }
    const updated = { ...settings, status: next };
    setSettings(updated);
    try {
      await adminUpdateMerchSettings(updated, token);
      toast.success(next === "live" ? "Shop is now LIVE" : "Shop set to Coming Soon");
    } catch {
      toast.error("Toggle failed");
      setSettings((s) => ({ ...s, status: settings.status }));
    }
  };

  const openNew = () => {
    const next = (products.reduce((m, x) => Math.max(m, x.sort_order || 0), 0) || 0) + 1;
    setEditing({ ...EMPTY, sort_order: next });
  };

  const saveProduct = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    if (Number(editing.price) < 0 || Number.isNaN(Number(editing.price))) return toast.error("Price must be a number");
    setSavingProduct(true);
    const payload = {
      name: editing.name.trim(),
      description: editing.description || "",
      price: Number(editing.price),
      image_url: editing.image_url || null,
      buy_url: editing.buy_url || "",
      badge: editing.badge || "",
      in_stock: !!editing.in_stock,
      sort_order: Number(editing.sort_order) || 999,
    };
    try {
      if (editing.id) {
        await adminUpdateMerchProduct(editing.id, payload, token);
        toast.success("Product updated");
      } else {
        await adminCreateMerchProduct(payload, token);
        toast.success("Product added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingProduct(false);
    }
  };

  const removeProduct = async (item) => {
    if (!window.confirm(`Remove "${item.name}"?`)) return;
    try {
      await adminDeleteMerchProduct(item.id, token);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminUploadImage(file, token);
      setEditing((c) => ({ ...c, image_url: data.url }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sorted = [...products].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const isLive = settings.status === "live";

  return (
    <div data-testid="admin-merch" className="space-y-8 pb-24">
      {/* Status bar */}
      <div className="border-2 border-[#222] bg-[#0F0F0F]">
        <div className="bg-black border-b-2 border-[#333] px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-2xl text-white">Merch Page Status</h3>
            <p className="text-white/50 text-xs uppercase tracking-widest mt-1">
              {isLive ? "Live — customers see your products" : "Coming Soon — customers see the teaser"}
            </p>
          </div>
          <button
            data-testid="merch-toggle-live"
            onClick={toggleLive}
            className={`inline-flex items-center gap-2 px-5 h-11 uppercase tracking-widest text-xs font-bold border-2 transition-colors ${
              isLive ? "bg-[#0A0A0A] border-[#E60000] text-white hover:bg-[#E60000]" : "bg-[#E60000] border-transparent text-white hover:border-white"
            }`}
          >
            {isLive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
            {isLive ? "Switch to Coming Soon" : "Go Live"}
          </button>
        </div>

        {/* Coming Soon copy editor */}
        <div className="p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Coming Soon Page Copy</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Eyebrow badge</label>
              <Input value={settings.eyebrow || ""} onChange={(e) => update("eyebrow")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Background image</label>
              <Input value={settings.background_image_url || ""} onChange={(e) => update("background_image_url")(e.target.value)} placeholder="/assets/photos/IMG_3967.jpeg" className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Headline line 1</label>
              <Input value={settings.title_line1 || ""} onChange={(e) => update("title_line1")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Headline accent (red)</label>
              <Input value={settings.title_accent || ""} onChange={(e) => update("title_accent")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Subtitle paragraph</label>
              <Textarea rows={3} value={settings.subtitle || ""} onChange={(e) => update("subtitle")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Tagline (small caps line)</label>
              <Input value={settings.tagline || ""} onChange={(e) => update("tagline")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Notify button URL</label>
              <Input value={settings.notify_url || ""} onChange={(e) => update("notify_url")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-1.5 block">Notify button label</label>
              <Input value={settings.notify_label || ""} onChange={(e) => update("notify_label")(e.target.value)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Preview item chips (4 cells)</label>
              <button onClick={addPreview} className="inline-flex items-center gap-1 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-8 uppercase tracking-widest text-[10px] font-bold">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
              {(settings.preview_items || []).map((p, idx) => (
                <div key={idx} className="flex items-center gap-1">
                  <Input value={p} onChange={(e) => togglePreview(e.target.value, idx)} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-9 text-white text-sm flex-1" />
                  <button onClick={() => removePreview(idx)} className="w-9 h-9 flex items-center justify-center bg-[#0A0A0A] border-2 border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button data-testid="merch-save-settings" onClick={saveSettings} disabled={savingSettings} className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50">
              <Save className="w-4 h-4" /> {savingSettings ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="border-2 border-[#222] bg-[#0F0F0F]">
        <div className="bg-black border-b-2 border-[#333] px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading text-2xl text-white">Products</h3>
            <p className="text-white/50 text-xs uppercase tracking-widest mt-1">{products.length} products · {isLive ? "showing live in shop" : "hidden until you go live"}</p>
          </div>
          <button data-testid="merch-product-add" onClick={openNew} className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-4 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>

        <div className="p-5">
          {products.length === 0 ? (
            <div className="text-center py-12 text-white/40 uppercase tracking-widest border-2 border-dashed border-[#222]">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-50" />
              No products yet — add your first item
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {sorted.map((it) => (
                <article key={it.id} data-testid={`merch-card-${it.id}`} className="border-2 border-[#222] bg-[#1A1A1A] p-3 flex gap-3 hover:border-[#E60000] transition-colors">
                  <div className="w-20 h-20 bg-black border border-[#333] overflow-hidden flex-shrink-0">
                    {it.image_url ? <img src={resolveImageUrl(it.image_url)} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImagePlus className="w-6 h-6" /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading text-base text-white truncate leading-tight">{it.name}</h4>
                    <div className="text-[#E60000] font-bold text-sm">${Number(it.price).toFixed(2)}</div>
                    <p className="text-white/50 text-xs line-clamp-1">{it.description}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {it.badge && <span className="text-[9px] uppercase tracking-widest text-[#E60000] font-bold border border-[#E60000] px-1.5">{it.badge}</span>}
                      {!it.in_stock && <span className="text-[9px] uppercase tracking-widest text-white/50 font-bold border border-[#444] px-1.5">Sold Out</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditing({ ...it })} data-testid={`merch-edit-${it.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:border-[#E60000] hover:text-[#E60000] text-white/70"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => removeProduct(it)} data-testid={`merch-delete-${it.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div data-testid="merch-editor" className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#141414] border-2 border-[#E60000] max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#333] bg-black sticky top-0 z-10">
              <h3 className="font-heading text-2xl text-white">{editing.id ? "Edit Product" : "New Product"}</h3>
              <button onClick={() => setEditing(null)} className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#E60000]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Photo</label>
                <div className="flex gap-4">
                  <div className="w-28 h-28 bg-black border-2 border-[#333] overflow-hidden flex-shrink-0">
                    {editing.image_url ? <img src={resolveImageUrl(editing.image_url)} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImagePlus className="w-8 h-8" /></div>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-10 uppercase tracking-widest text-[10px] font-bold disabled:opacity-50">
                      <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Photo"}
                    </button>
                    <Input value={editing.image_url || ""} onChange={(e) => setEditing((c) => ({ ...c, image_url: e.target.value }))} placeholder="Or paste an image URL" className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Name</label>
                <Input value={editing.name} onChange={(e) => setEditing((c) => ({ ...c, name: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Price ($)</label>
                  <Input type="number" step="0.5" min="0" value={editing.price} onChange={(e) => setEditing((c) => ({ ...c, price: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Sort order</label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing((c) => ({ ...c, sort_order: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Description</label>
                <Textarea rows={4} value={editing.description} onChange={(e) => setEditing((c) => ({ ...c, description: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Badge (optional)</label>
                  <Input value={editing.badge || ""} onChange={(e) => setEditing((c) => ({ ...c, badge: e.target.value }))} placeholder='e.g. "New", "Limited"' className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
                <div className="flex items-end pb-1">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!editing.in_stock} onChange={(e) => setEditing((c) => ({ ...c, in_stock: e.target.checked }))} className="w-4 h-4 accent-[#E60000]" />
                    <span className="text-xs uppercase tracking-widest text-white/70 font-bold">In stock</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Buy URL (Shopify / Square / Stripe Payment Link)</label>
                <Input value={editing.buy_url || ""} onChange={(e) => setEditing((c) => ({ ...c, buy_url: e.target.value }))} placeholder="https://buy.stripe.com/..." className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white text-sm" />
                <div className="text-[10px] text-white/40 mt-1">If blank, the Buy button will show "Coming Soon" instead.</div>
              </div>
            </div>

            <div className="px-6 py-4 border-t-2 border-[#333] bg-black sticky bottom-0 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white/70 hover:text-white px-4 h-10 uppercase tracking-widest text-xs font-bold">Cancel</button>
              <button onClick={saveProduct} disabled={savingProduct} data-testid="merch-save-product" className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50">
                <Save className="w-4 h-4" /> {savingProduct ? "Saving…" : editing.id ? "Save Changes" : "Create Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
